import React, { useState, useEffect } from 'react';
import { Trash2, Plus, Upload, Share2, Download, RotateCcw } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { downloadPDF, sharePDF } from './utils/pdfGenerator';
import AdSpace from './components/AdSpace';
import './App.css';

// --- STATIC PAGES COMPONENTS ---
const AboutUs = () => (
  <section className="card static-page">
    <h2>About Us</h2>
    <p>Welcome to <strong>estimator.co.in</strong>, a professional estimate generator brought to you by <strong>pvalue005 technologies llp</strong>.</p>
    <p>We are a team of young enthusiasts working for pvalue005 technologies llp, solving real-world problems with AI and analytics solutions.</p>
    <p>We built this tool to help freelancers, contractors, and small business owners generate beautiful PDF estimates instantly. There are no sign-ups, no subscriptions, and no complex software required.</p>
    <p>Because all data processing happens right in your own browser, your business and client data remains completely private and secure. Our goal is to make your billing process as simple and fast as possible.</p>
  </section>
);

const ContactUs = () => (
  <section className="card static-page">
    <h2>Contact Us</h2>
    <p>If you have any questions, feedback, or need support using our Estimate Generator, we would love to hear from you.</p>
    <p><strong>Email:</strong> pvalue005.ai@gmail.com</p>
    <p>We aim to respond to all inquiries as quickly as possible.</p>
  </section>
);

const PrivacyPolicy = () => (
  <section className="card static-page">
    <h2>Privacy Policy</h2>
    <p><strong>Last Updated: {new Date().toLocaleDateString()}</strong></p>
    <p>Your privacy is our priority. This Privacy Policy outlines how <strong>pvalue005 technologies llp</strong> handles your information when you use estimator.co.in.</p>
    
    <h3>1. Data Storage</h3>
    <p>Our application operates entirely on your local device (client-side). We do not collect, store, or transmit your business details, client information, or estimate data to any external servers. Your draft is saved temporarily in your own browser's local storage solely for your convenience.</p>
    
    <h3>2. Third-Party Services (Google AdSense & Analytics)</h3>
    <p>To support this tool, we use Google AdSense to display advertisements. Google uses cookies to serve ads based on your prior visits to our website or other websites. You can opt out of personalized advertising by visiting Google's Ads Settings.</p>
    <p>We also use Google Analytics to monitor general website traffic so we can improve the tool. This data is anonymized and does not include your personal estimate data.</p>
  </section>
);

const Footer = () => (
  <footer className="app-footer">
    <div className="footer-links">
      <Link to="/">Home</Link>
      <span className="divider">|</span>
      <Link to="/about">About Us</Link>
      <span className="divider">|</span>
      <Link to="/contact">Contact Us</Link>
      <span className="divider">|</span>
      <Link to="/privacy">Privacy Policy</Link>
    </div>
    <p className="footer-copyright">
      © {new Date().getFullYear()} pvalue005 technologies llp. All rights reserved.
    </p>
  </footer>
);

// --- SEO & CONTENT COMPONENT (To satisfy AdSense) ---
const HomePageContent = () => (
  <section className="seo-content-section card">
    <h2 style={{ textAlign: 'center' }}>Free Estimate generator</h2>
    <p>
      Whether you are a freelancer, a contractor, or running a small business, sending a clear and accurate estimate is the first step to winning a client's trust. The <strong>estimator.co.in</strong> tool is designed by the team at pvalue005 to help you generate professional, math-perfect estimates in seconds.
    </p>
  </section>
);

const HomePageBottomContent = () => (
  <section className="seo-content-section card">
    <h3>What is an Estimate?</h3>
    <p>
      An estimate is a preliminary document provided to a client outlining the expected costs of a project, service, or product. Unlike a final invoice, an estimate is not a legally binding bill. It is an educated projection that helps clients understand your pricing before they commit to the work.
    </p>

    <h3>How to Use This Free Generator</h3>
    <ul className="content-list">
      <li><strong>Add Your Business Details:</strong> Upload your logo and input your brand's contact information to make the document look official.</li>
      <li><strong>Enter Client Information:</strong> Clearly state who the estimate is for. This prevents miscommunication.</li>
      <li><strong>Itemize Your Services:</strong> Break down the project into specific tasks or items. Enter the description, quantity, and rate. Our tool will automatically calculate the line-item amounts.</li>
      <li><strong>Apply Taxes and Discounts:</strong> If applicable, enter your tax percentage or a flat discount. The math is handled for you instantly.</li>
      <li><strong>Save or Share:</strong> Once complete, download the perfect PDF or share it directly with your client via mobile.</li>
    </ul>

    <h3>Estimate vs. Invoice: What’s the Difference?</h3>
    <p>
      Many business owners confuse estimates with invoices. An <strong>estimate</strong> is sent <em>before</em> the work begins to agree on the budget. An <strong>invoice</strong> is sent <em>after</em> the work is completed (or at specific milestones) to request actual payment. 
    </p>

    <h3>Why Trust Our Tool?</h3>
    <p>
      At pvalue005, we believe in building tools that solve real-world problems. We noticed that small business owners were wasting hours formatting spreadsheets just to send a simple quote. Our generator runs entirely in your browser—meaning your financial data is never stored on our servers, ensuring 100% privacy and security.
    </p>
  </section>
);

// --- MAIN APP COMPONENT ---
function App() {
  // --- STATE ---
  const [business, setBusiness] = useState({ name: '', email: '', phone: '', address: '', logo: null });
  const [client, setClient] = useState({ name: '', email: '', phone: '', address: '' });
  const [meta, setMeta] = useState({ estimateNumber: 'EST-001', date: new Date().toISOString().substr(0, 10) });
  const [items, setItems] = useState([{ id: 1, description: '', quantity: '', rate: '' }]);
  const [financials, setFinancials] = useState({ taxRate: 0, discount: 0 });
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});

  // --- AUTO LOAD ---
  useEffect(() => {
    const savedData = localStorage.getItem('estimateDraft');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      setBusiness(parsed.business);
      setClient(parsed.client);
      setMeta(parsed.meta);
      setItems(parsed.items);
      setFinancials(parsed.financials);
      setNotes(parsed.notes);
    }
  }, []);

  // --- AUTO SAVE ---
  useEffect(() => {
    const dataToSave = { business, client, meta, items, financials, notes };
    localStorage.setItem('estimateDraft', JSON.stringify(dataToSave));
  }, [business, client, meta, items, financials, notes]);

  // --- CALCULATIONS ---
  const totals = (() => {
    const subtotal = items.reduce((acc, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      return acc + (qty * rate);
    }, 0);
  
    const taxAmount = (subtotal * financials.taxRate) / 100;
    const total = subtotal + taxAmount - financials.discount;
    return { subtotal, taxAmount, total };
  })();

  // --- HANDLERS ---
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (x) => setBusiness({ ...business, logo: x.target.result });
      reader.readAsDataURL(file);
    }
  };

  const updateItem = (id, field, value) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addItem = () => setItems([...items, { id: Date.now(), description: '', quantity: '', rate: '' }]);
  const removeItem = (id) => setItems(items.filter(item => item.id !== id));

  // --- ACTION HANDLERS ---
  const handleDownload = () => {
    if (validateForm()) {
      downloadPDF({ business, client, meta, items, totals: { ...totals, ...financials }, notes });
    }
  };

  const handleShare = () => {
    if (validateForm()) {
      sharePDF({ business, client, meta, items, totals: { ...totals, ...financials }, notes });
    }
  };

  const handleClear = () => {
    if(window.confirm("Are you sure you want to clear the entire form? This cannot be undone.")) {
        localStorage.removeItem('estimateDraft');
        setClient({ name: '', email: '', phone: '', address: '' });
        setItems([{ id: Date.now(), description: '', quantity: '', rate: '' }]);
        setFinancials({ taxRate: 0, discount: 0 });
        setNotes('');
        setMeta({ ...meta, estimateNumber: 'EST-001' });
    }
  };

  // --- VALIDATION HELPER ---
  const validateForm = () => {
    let newErrors = {};
    let isValid = true;

    if (items.length === 0) {
      alert("Please add at least one item.");
      return false;
    }

    items.forEach((item) => {
      if (!item.description.trim()) {
        newErrors[`${item.id}-desc`] = true;
        isValid = false;
      }
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        newErrors[`${item.id}-qty`] = true;
        isValid = false;
      }
      if (!item.rate || parseFloat(item.rate) <= 0) {
        newErrors[`${item.id}-rate`] = true;
        isValid = false;
      }
    });

    setErrors(newErrors); 
    
    if (!isValid) {
      const firstError = document.querySelector('.input-error');
      if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return isValid;
  };

  return (
    <Router>
      <div className="app-container">
        <nav className="navbar" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px 0' }}>
          {/* Clicking the logo brings users back to the Home page */}
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-block' }}>
            <img 
              src={process.env.PUBLIC_URL + '/logo.PNG'} /* Make sure your image in the public folder is named logo.png */
              alt="Estimator.co.in Logo" 
              style={{ 
                maxWidth: '100%',     /* Ensures it doesn't break out of mobile screens */
                maxHeight: '70px',    /* Scales it down. Change 70px to make it bigger/smaller */
                height: 'auto',       /* Maintains the exact aspect ratio */
                objectFit: 'contain', 
                display: 'block'
              }} 
            />
          </Link>
        </nav>

        {/* -------------------             TEMPORARILY HIDE UNTIL APPROVED * ---------------- /}
        {/* <AdSpace position="top-banner" /> */}

        {/* --- ROUTING LOGIC STARTS HERE --- */}
        <Routes>
          
          {/* 1. HOME PAGE ROUTE (Contains all your existing UI) */}
          <Route path="/" element={
            <>

            {/* --- ADD THE NEW CONTENT HERE --- */}
              <HomePageContent />
            <div className="main-grid">
              <div className="editor-section">
                
                {/* Business Info */}
                <section className="card">
                  <h2>Business Details</h2>
                  <div className="logo-upload-container">
                     {business.logo ? (
                       <img src={business.logo} alt="Logo" className="logo-preview" />
                     ) : (
                       <label className="logo-placeholder">
                         <Upload size={24} /> <span>Upload Logo</span>
                         <input type="file" accept="image/*" onChange={handleLogoUpload} hidden />
                       </label>
                     )}
                  </div>
                  <div className="input-group">
                    <input placeholder="Business Name" value={business.name} onChange={e => setBusiness({...business, name: e.target.value})} />
                    <input placeholder="Phone" value={business.phone} onChange={e => setBusiness({...business, phone: e.target.value})} />
                  </div>
                  <input placeholder="Address" className="full-width" value={business.address} onChange={e => setBusiness({...business, address: e.target.value})} />
                  <input placeholder="Email" className="full-width" value={business.email} onChange={e => setBusiness({...business, email: e.target.value})} />
                </section>

                {/* Client & Meta Info */}
                <section className="card">
                  <h3>Bill To</h3>
                  <div className="input-group">
                    <input placeholder="Client Name" value={client.name} onChange={e => setClient({...client, name: e.target.value})} />
                    <input placeholder="Client Phone" value={client.phone} onChange={e => setClient({...client, phone: e.target.value})} />
                  </div>
                  <div className="input-group">
                    <input placeholder="Client Email" value={client.email} onChange={e => setClient({...client, email: e.target.value})} />
                    <input placeholder="Client Address" value={client.address} onChange={e => setClient({...client, address: e.target.value})} />
                  </div>

                  <h3>Estimate Details</h3>
                  <div className="input-group">
                    <input type="text" placeholder="Estimate #" value={meta.estimateNumber} onChange={e => setMeta({...meta, estimateNumber: e.target.value})} />
                    <input type="date" value={meta.date} onChange={e => setMeta({...meta, date: e.target.value})} />
                  </div>
                </section>

                {/* Items Section */}
                <section className="card">
                  <h3>Items</h3>
                  <div className="items-header">
                    <span className="col-desc">Description <span className="required-star">*</span></span>
                    <span className="col-qty">Quantity <span className="required-star">*</span></span>
                    <span className="col-rate">Rate (₹) <span className="required-star">*</span></span>
                    <span className="col-amount">Amount</span>
                    <span className="col-action"></span>
                  </div>

                  {items.map((item) => (
                    <div key={item.id} className="item-row">
                      <div className="input-wrap col-desc">
                          <label className="mobile-label">Description <span className="required-star">*</span></label>
                          <input 
                            className={`item-desc ${errors[`${item.id}-desc`] ? 'input-error' : ''}`} 
                            placeholder="Item name" 
                            value={item.description} 
                            onChange={(e) => {
                               setErrors({...errors, [`${item.id}-desc`]: false});
                               updateItem(item.id, 'description', e.target.value);
                            }} 
                          />
                      </div>
                      <div className="input-wrap col-qty">
                          <label className="mobile-label">Qty <span className="required-star">*</span></label>
                          <input 
                            type="number" 
                            className={`item-qty ${errors[`${item.id}-qty`] ? 'input-error' : ''}`}
                            placeholder="0" 
                            value={item.quantity} 
                            onChange={(e) => {
                               setErrors({...errors, [`${item.id}-qty`]: false});
                               updateItem(item.id, 'quantity', e.target.value);
                            }} 
                          />
                      </div>
                      <div className="input-wrap col-rate">
                          <label className="mobile-label">Rate <span className="required-star">*</span></label>
                          <input 
                            type="number" 
                            className={`item-rate ${errors[`${item.id}-rate`] ? 'input-error' : ''}`} 
                            placeholder="0.00" 
                            value={item.rate} 
                            onChange={(e) => {
                               setErrors({...errors, [`${item.id}-rate`]: false});
                               updateItem(item.id, 'rate', e.target.value);
                            }} 
                          />
                      </div>
                      <div className="col-amount">
                          <label className="mobile-label">Amount</label>
                          <span className="item-total">
                              ₹{((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)).toFixed(2)}
                          </span>
                      </div>
                      <button className="btn-icon" onClick={() => removeItem(item.id)}>
                          <Trash2 size={16}/>
                      </button>
                    </div>
                  ))}
                  <button className="btn-secondary" onClick={addItem}><Plus size={16}/> Add Item</button>
                </section>
                
                {/* Totals & Notes */}
                <section className="card totals-section">
                  <div className="notes-area">
                    <textarea placeholder="Notes (Optional)" value={notes} onChange={e => setNotes(e.target.value)}></textarea>
                  </div>
                  <div className="calculations">
                    <div className="calc-row">
                      <span>Subtotal:</span>
                      <span>₹{totals.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="calc-row">
                      <span>Tax (%):</span>
                      <input type="number" className="small-input" value={financials.taxRate} onChange={e => setFinancials({...financials, taxRate: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div className="calc-row">
                      <span>Discount:</span>
                      <input type="number" className="small-input" value={financials.discount} onChange={e => setFinancials({...financials, discount: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div className="calc-row total highlight">
                      <span>Total (INR):</span>
                      <span>₹{totals.total.toFixed(2)}</span>
                    </div>
                  </div>
                </section>

                <section className="action-bar-container">
                  <button className="btn-danger-outline" onClick={handleClear}>
                      <RotateCcw size={18} /> <span>Clear</span>
                  </button>
                  <div className="right-actions">
                      <button className="btn-secondary" onClick={handleDownload}>
                          <Download size={18} /> <span>Save</span>
                      </button>
                      <button className="btn-primary" onClick={handleShare}>
                          <Share2 size={18} /> <span>Share</span>
                      </button>
                  </div>
                </section>

              </div>

              {/* RIGHT COLUMN: ADS */}
              <div className="sidebar-ads">
                <AdSpace position="sidebar-1" />
                <AdSpace position="sidebar-2" />
              </div>
            </div>
            {/* --- ADD THE NEW CONTENT HERE --- */}
            <HomePageBottomContent />
            </>
          } />

          {/* 2. STATIC PAGES ROUTES */}
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

        </Routes>
        
        {/* FOOTER - Shows on every page */}
        <Footer />
        
      </div>
    </Router>
  );
}

export default App;